import type { Application } from '@3d4medical/websdk';
import { useBridge } from '@completeanatomy/bridge';
import { useCallback, useEffect } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { getModelIndex } from '../utils/getModelIndex';

export const useModelActivation = (app: Application | null) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { modelId } = useParams();
  const [searchParams] = useSearchParams();
  const bridge = useBridge();

  const focusCanvas = () => {
    const canvas = document.getElementById('model-canvas') as HTMLCanvasElement;

    if (canvas) {
      canvas.focus();
    }
  };

  const activateModel = useCallback(async () => {
    if (!app || !modelId) {
      console.warn('No app instance or model ID available');

      return;
    }

    console.log('Activating model with ID:', modelId);

    try {
      const screenManager = app.getScreenManager();

      // Get query parameters
      const bodyRegion = searchParams.get('bodyRegion');
      const systemId = searchParams.get('systemId');

      await screenManager.activateModel(
        modelId,
        undefined,
        true,
        true,
        bodyRegion ? JSON.parse(bodyRegion) : undefined,
        systemId || undefined
      );

      console.log('Model activated successfully');

      focusCanvas();

      const index = getModelIndex(modelId, bodyRegion, systemId);

      if (index !== -1) {
        bridge?.emit('BridgeObjectChanged', {
          identifier: 'modelState',
          keyPath: 'selectedModelIndex',
          changes: index,
        });
      }

      const model_version = app.getActiveModel()?.getVersion() || '1';

      //TO-DO: We need to remove this after backend is updated to skip model-version (For search feature)
      bridge?.emit('BridgeObjectChanged', {
        identifier: 'libraryState',
        keyPath: 'openedResources.screen',
        changes: {
          model_version: model_version,
          author: {
            name: '',
            image: '',
          },
          name: '',
          information: '',
          model_identifier: modelId,
        },
      });
    } catch (error) {
      console.error('Failed to activate model:', error);
      navigate('/content-unavailable');
    }
  }, [app, modelId, searchParams, bridge, navigate]);

  useEffect(() => {
    if (!app || !modelId) {
      return;
    }

    // Check if current path is /models route
    const isModelsPath = location.pathname.includes('/models/');

    if (isModelsPath) {
      activateModel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, modelId, location.pathname, searchParams]);
};
